import toast from 'react-hot-toast';
import { Market } from '@/types/MatchData';
import { BetState } from '@/types/Bet';
import { sportsIconMap } from '@/utils/constants';
type ToastType = 'success' | 'error' | 'loading' | 'custom';

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function throttle<T extends (...args: any[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => false);
  } else {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'absolute';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      textArea.remove();
      return Promise.resolve(true);
    } catch (error) {
      textArea.remove();
      return Promise.resolve(false);
    }
  }
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

export function parseJWT(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = parseJWT(token);
  if (!payload?.exp) return true;
  return Date.now() >= payload.exp * 1000;
}

export const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

export const showToast = (message: string, type: ToastType = 'success'): string => {
  switch (type) {
    case 'success':
      return toast.success(message);

    case 'error':
      return toast.error(message);

    case 'loading':
      return toast.loading(message);

    case 'custom':
      return toast(message);

    default:
      return toast(message);
  }
};

export const getSportIdByName = (name: string) => {
  switch (name) {
    case 'Cricket':
      return 4;
    case 'Football':
      return 1;
    case 'Tennis':
      return 2;
    default:
      return 4;
  }
};

export const formatMatchTime = (dateInput: string | number | Date): string => {
  if (!dateInput) return '';

  const date = new Date(dateInput);

  if (isNaN(date.getTime())) return '';

  const datePart = date.toLocaleDateString('en-GB');
  const timePart = date.toLocaleTimeString('en-GB', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return `${datePart} ${timePart}`;
};

export const sortMarketsBySport = (sportName?: string, markets?: Market[]): Market[] => {
  if (!Array.isArray(markets) || markets.length === 0) return [];

  const safeMarkets = [...markets];

  const getMarketValue = (market: Market): number => {
    const typeMatch = market.marketType?.match(/(\d+)(?=$|_)/);
    if (typeMatch) {
      const raw = Number(typeMatch[1]);

      // 05 → 0.5 , 15 → 1.5 , 25 → 2.5
      if (!Number.isNaN(raw)) {
        return raw >= 10 ? raw / 10 : raw;
      }
    }

    // ---------- 2️⃣ Try handicap / line fields ----------
    const lineValue =
      (market as any)?.line ?? (market as any)?.handicap ?? (market.runners?.[0] as any)?.line;

    if (typeof lineValue === 'number') {
      return lineValue;
    }

    // ---------- 3️⃣ Fallback → parse from marketName ----------
    const nameMatch = market.marketName?.match(/([\d.]+)/);
    if (nameMatch) {
      return Number(nameMatch[1]);
    }

    // ---------- 4️⃣ Unknown markets go last ----------
    return Number.MAX_SAFE_INTEGER;
  };

  /**
   * Tennis → sort by Set number
   * Example: Set 1 Winner, Set 2 Winner
   */
  const getSetNumber = (market: Market): number => {
    const match = market.marketName?.match(/set\s*(\d+)/i);
    return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
  };

  // ---------- SPORT BASED SORT ----------
  const sport = sportName?.toLowerCase();

  if (sport === 'tennis') {
    return safeMarkets.sort((a, b) => getSetNumber(a) - getSetNumber(b));
  }

  // Soccer / Football / generic line markets
  if (sport === 'soccer' || sport === 'football') {
    return safeMarkets.sort((a, b) => getMarketValue(a) - getMarketValue(b));
  }

  // Default: numeric market sort (safe fallback)
  return safeMarkets.sort((a, b) => getMarketValue(a) - getMarketValue(b));
};

export const formatCompactNumber = (value?: number | string | null) => {
  const num = Number(value);
  if (!num && num !== 0) return '-';

  return new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(num);
};

export const getDeltaExposure = (
  selectionId: number | string,
  marketId: number | string | undefined,
  betDetails: BetState
): number => {
  const bet = betDetails?.selectedBet;
  if (!bet || bet.marketId !== marketId) return 0;

  const isSameSelection = bet.selectionId == selectionId;
  const isBack = bet.betOn === 'BACK';
  const stake = betDetails?.stake || 0;

  /* ---------- BOOKMAKER ---------- */
  if (bet.marketCategory === 'BOOKMAKER') {
    if (isSameSelection) {
      const value = ((bet.rate || 0) / 100) * stake;
      return isBack ? value : -value;
    }
    return isBack ? -stake : stake;
  }

  /* ---------- NORMAL ---------- */
  if (bet.marketType === 'NORMAL') {
    if (isSameSelection) {
      const value = ((bet.rate || 0) - 1) * stake;
      return isBack ? value : -value;
    }
    return isBack ? -stake : stake;
  }

  /* ---------- FANCY / OTHER ---------- */
  if (isSameSelection) {
    const value = ((bet.fancyPercentage || 0) / 100) * stake;
    // return isBack ? value : -value;
    return 0;
  }

  return 0;
};

export const formatNumber = (value: number | string): string => {
  const num = Number(value);

  if (isNaN(num)) return '0';

  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
  }).format(num);
};

export function formatDateTime(
  value: string | number | Date,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!value) return '';

  const date = new Date(value);

  // invalid date guard
  if (isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options,
  }).format(date);
}

export const getMarketRunnerStatus = (
  marketStatus?: string,
  runnerStatus?: string
): string | null => {
  const market = marketStatus?.toUpperCase();
  const runner = runnerStatus?.toUpperCase();

  const valid = ['OPEN', 'ACTIVE', ''];

  const isRunnerActive = valid.includes(runner ?? '');
  const isMarketActive = valid.includes(market ?? '');

  // ✅ both active
  if (isRunnerActive && isMarketActive) return null;

  // ✅ both NOT active → priority to runner
  if (!isRunnerActive && !isMarketActive) return runner ?? null;

  // ✅ only runner failed
  if (!isRunnerActive) return runner ?? null;

  // ✅ only market failed
  return market ?? null;
};

export const getSportIcon = (sportName?: string | null): string | null => {
  if (!sportName) return null;

  return sportsIconMap[sportName] ?? null;
};

export const getPlatform = (): 'Mobile' | 'Desktop' => {
  if (typeof window === 'undefined') return 'Desktop';

  const userAgent = navigator.userAgent.toLowerCase();

  const isMobile = /iphone|ipad|ipod|android|blackberry|opera mini|iemobile|mobile/i.test(
    userAgent
  );

  return isMobile ? 'Mobile' : 'Desktop';
};
